// The agent's reasoning. Streaming: expanded "⚛ Thinking…" card listing every
// step with a status dot. Settled: collapsed to "Completed N steps", click to
// expand. Pending steps render hollow (nothing emits them yet).

import { type FC, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Loader, Text } from '@kula-ai/components';
import { Atom01, ChevronDown, ChevronRight } from '@kula-ai/icons-core';
import {
  dropRetriedErrors,
  formatElapsed,
  formatTokens,
  settledHeaderLabel,
  shouldShow,
  streamingHeaderLabel,
  SHOW_ELAPSED_AT_S,
} from './stepLabels';
import { StepRow } from './StepRow';
import { useElapsedSeconds } from '../../../hooks';
import type { StepTimelineProps } from './StepTimeline.types';

export const StepTimeline: FC<StepTimelineProps> = ({
  steps,
  streaming,
  approxDownTokens,
}) => {
  const visible = dropRetriedErrors(steps.filter(shouldShow), streaming);
  // Open by default in both modes — the step list is the point of the panel.
  const [expanded, setExpanded] = useState(true);

  const active = streaming
    ? visible.find((s) => s.status === 'active')
    : undefined;
  const elapsedSec = useElapsedSeconds(active?.id);

  if (visible.length === 0) return null;

  const headerLabel = streaming
    ? streamingHeaderLabel(visible, active, elapsedSec)
    : settledHeaderLabel(visible);

  // Footer meta: how long the running step has taken, and how much prose has
  // streamed down — both are liveness signals when no new step has started.
  const meta: string[] = [];
  if (streaming && elapsedSec >= SHOW_ELAPSED_AT_S) {
    meta.push(formatElapsed(elapsedSec));
  }
  if (streaming && (approxDownTokens ?? 0) > 0) {
    meta.push(`↓ ${formatTokens(approxDownTokens ?? 0)} tokens`);
  }

  // Streaming reads as a card the agent is filling in; settled collapses to a
  // quiet inline footnote (no border/box) so it doesn't look like a separate
  // accordion.
  const boxProps = streaming
    ? {
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'border-color',
        bg: 'base-white',
        px: '12px',
        py: '8px',
      }
    : {};

  return (
    <Box
      {...boxProps}
      mb="8px"
      w="100%"
      maxW="520px"
      data-test-id="agentforge-step-timeline"
    >
      {/* Only streaming is interactive (toggle the live step list). Once
          settled there's no sub-history to open, so the header is static. */}
      <Flex
        {...(streaming
          ? {
              as: 'button',
              type: 'button',
              onClick: () => setExpanded((v) => !v),
              'aria-expanded': expanded,
              'aria-label': expanded ? 'Hide steps' : 'Show steps',
            }
          : {})}
        align="center"
        gap="8px"
        w="100%"
        textAlign="left"
      >
        <Box color="secondary-400" flexShrink={0} display="inline-flex">
          {streaming && !expanded ? (
            <ChevronRight size={14} color="currentColor" />
          ) : (
            <ChevronDown size={14} color="currentColor" />
          )}
        </Box>
        <Box flexShrink={0} display="inline-flex">
          <Atom01 size={15} color="currentColor" />
        </Box>
        {/* Settled reads as a quiet footnote (italic, muted); live reads as
            the agent speaking. */}
        {streaming ? (
          <Text
            variant="p"
            className="text-[14px] font-semibold text-secondary-700 truncate"
            title={headerLabel}
          >
            {headerLabel}
          </Text>
        ) : (
          <Text
            variant="small"
            className="text-[12px] italic text-secondary-400 truncate"
            title={headerLabel}
          >
            {headerLabel}
          </Text>
        )}
        {streaming && (
          <Box ml="auto" flexShrink={0}>
            <Loader size={16} />
          </Box>
        )}
      </Flex>

      {streaming && expanded && (
        <Flex direction="column" gap="0" mt="8px" pl="4px">
          {visible.map((step) => (
            <StepRow
              key={step.id}
              step={step}
              elapsedSec={step === active ? elapsedSec : undefined}
            />
          ))}
        </Flex>
      )}

      {meta.length > 0 && (
        <Flex justify="flex-end" mt="6px">
          <Text variant="small" className="text-[12px] text-secondary-400">
            {meta.join(' · ')}
          </Text>
        </Flex>
      )}
    </Box>
  );
};
